<template>
    <div class="cashier-crypto-deposit">
        <div class="deposit-info">
            <div class="info-qrcode">
                <transition name="fade" mode="out-in">
                    <div v-if="loading" class="qrcode-loading" key="loading"></div>
                    
                    <div v-else-if="paymentData" class="qrcode-content" key="data">
                        <QRCode v-bind:value="paymentData.pay_address" v-bind:options="{ width: 140, height: 140, margin: 0 }" />
                    </div>

                    <div v-else class="qrcode-content empty-qr" key="empty">
                        <span>READY</span>
                    </div>
                </transition>
            </div>
            
            <div class="info-text">
                <div class="text-header">
                    <img v-bind:src="require('@/assets/img/cashier/' + modalsData.currency + '.png')" />
                    {{cashierGetName}}
                </div>
                <p>Always double-check the address and the amount before sending. We cannot recover funds sent to the wrong address.</p>
                
                <p v-if="paymentData">
                    Send exactly <span class="text-green-gradient">{{ paymentData.pay_amount }} {{ modalsData.currency.toUpperCase() }}</span>. 
                    Credits added automatically after 1 confirmation.
                </p>
                <p v-else>
                    Enter the USD amount below and click <b>GENERATE</b> to get your personal deposit address.
                </p>
            </div>
        </div>

        <div class="deposit-rate">
            <div class="rate-title">ENTER DEPOSIT AMOUNT (USD)</div>
            <div class="rate-content">
                <div class="content-element">
                    <div class="element-content">
                        <span class="text-green-gradient">$</span>
                        <input v-model="amount" type="number" placeholder="10.00" @input="paymentData = null" />
                    </div>
                </div>
                
                <button @click="generateAddress" class="button-generate" :disabled="loading || amount < 1">
                    <div class="button-inner">
                        {{ loading ? 'GENERATING...' : 'GENERATE ADDRESS' }}
                    </div>
                </button>
            </div>
        </div>

        <div v-if="paymentData" class="deposit-address">
            <div class="address-title">Your Personal {{ cashierGetName }} Address</div>
            <div class="address-input">
                <div class="input-content">
                    <input :value="paymentData.pay_address" type="text" readonly />
                    <button v-on:click="modalCopyButton()">
                        <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.5083 8.74064C12.2654 8.49634 11.9961 8.27977 11.7054 8.09492C10.9354 7.60186 10.0403 7.33985 9.12591 7.33985C7.85684 7.33749 6.63934 7.84172 5.74354 8.74064L1.39771 13.0899C0.503433 13.9859 0.000801741 15.1999 9.90297e-07 16.4658C-0.00168059 19.1074 2.1384 21.2503 4.78008 21.252C6.04719 21.2564 7.26357 20.7545 8.15905 19.858L11.7464 16.2707C11.8116 16.206 11.848 16.1179 11.8475 16.0261C11.8464 15.8374 11.6925 15.6854 11.5038 15.6864H11.3672C10.6177 15.6891 9.87486 15.5451 9.18061 15.2628C9.05269 15.2101 8.90567 15.2399 8.80822 15.3379L6.22872 17.9208C5.42773 18.7218 4.12907 18.7218 3.32808 17.9208C2.52709 17.1199 2.52709 15.8212 3.32808 15.0202L7.69101 10.6607C8.49136 9.86138 9.7879 9.86138 10.5882 10.6607C11.1276 11.1684 11.969 11.1684 12.5083 10.6607C12.7404 10.4285 12.8811 10.1204 12.9047 9.79291C12.9296 9.40154 12.7853 9.01827 12.5083 8.74064Z" />
                            <path d="M19.0989 2.15102C17.2308 0.282992 14.2022 0.282992 12.3341 2.15102L8.75016 5.73154C8.65175 5.83043 8.62336 5.97917 8.67842 6.10737C8.73283 6.23593 8.85999 6.31845 8.99956 6.31577H9.12595C9.87458 6.31441 10.6162 6.45946 11.3091 6.74285C11.437 6.7955 11.5841 6.76579 11.6815 6.6677L14.2542 4.09845C15.0552 3.29746 16.3538 3.29746 17.1548 4.09845C17.9558 4.89944 17.9558 6.1981 17.1548 6.99909L13.9501 10.2004L13.9227 10.2311L12.8021 11.3449C12.0018 12.1443 10.7052 12.1443 9.90488 11.3449C9.3655 10.8373 8.52415 10.8373 7.9848 11.3449C7.75127 11.5789 7.61045 11.8897 7.58847 12.2195C7.56349 12.6109 7.70782 12.9942 7.9848 13.2718C8.38026 13.669 8.84293 13.9928 9.35141 14.2285C9.42315 14.2626 9.4949 14.29 9.56665 14.3207C9.63839 14.3515 9.71355 14.3754 9.78529 14.4027C9.85704 14.4301 9.93219 14.454 10.0039 14.4745L10.2055 14.5291C10.3422 14.5633 10.4789 14.5906 10.6189 14.6145C10.7876 14.6396 10.9576 14.6555 11.128 14.6623H11.3671H11.3876L11.5926 14.6384C11.6678 14.635 11.7464 14.6179 11.8352 14.6179H11.9514L12.1871 14.5838L12.2964 14.5633L12.4946 14.5223H12.5322C13.3714 14.3115 14.1377 13.877 14.7495 13.265L19.0987 8.91576C20.9669 7.04773 20.9669 4.01905 19.0989 2.15102Z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex';
    import QRCode from '@/components/QRCode';
    import axios from 'axios';

    export default {
        name: 'CashierCryptoDeposit',
        components: {
            QRCode
        },
        data() {
            return {
                amount: 10,
                loading: false,
                paymentData: null
            }
        },
        methods: {
            ...mapActions(['notificationShow']),
            
            async generateAddress() {
                if (this.amount < 1) {
                    this.notificationShow({ type: 'error', message: 'Minimum deposit is $1.' });
                    return;
                }

                this.loading = true;
                try {
                    // Adjust URL based on environment
                    const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '';
                    
                    const res = await axios.post(`${baseUrl}/api/deposits/create`, {
                        amount: this.amount,
                        currency: this.modalsData.currency
                    }, {
                        headers: { 'Authorization': `Bearer ${this.$store.state.auth.token}` }
                    });

                    if (res.data.success) {
                        this.paymentData = res.data.data;
                        this.notificationShow({ type: 'success', message: 'Address generated successfully!' });
                    }
                } catch (err) {
                    this.notificationShow({ 
                        type: 'error', 
                        message: err.response?.data?.message || 'Failed to connect to payment server.' 
                    });
                } finally {
                    this.loading = false;
                }
            },

            modalCopyButton() {
                if (!this.paymentData) return;
                const el = document.createElement('textarea');
                el.value = this.paymentData.pay_address;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);

                this.notificationShow({ type: 'success', message: 'Copied to your clipboard.' });
            }
        },
        computed: {
            ...mapGetters(['modalsData']),
            cashierGetName() {
                const names = {
                    btc: 'Bitcoin',
                    eth: 'Ethereum',
                    ltc: 'Litecoin',
                    usdt: 'Tether',
                    usdc: 'USD Coin'
                };
                return names[this.modalsData.currency] || this.modalsData.currency.toUpperCase();
            }
        }
    }
</script>

<style scoped>
    .cashier-crypto-deposit {
        width: 100%;
        margin-top: 20px;
    }

    .deposit-info {
        display: flex;
        align-items: center;
    }

    .info-qrcode {
        width: 150px;
        height: 150px;
        background-color: #ffffff;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .empty-qr {
        color: #000;
        font-weight: 800;
        font-size: 14px;
    }

    .qrcode-loading {
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;
        background: #eee;
    }

    .qrcode-loading::after {
        content: '';
        position: absolute;
        top: 0; left: 0; width: 100%; height: 100%;
        background: linear-gradient(to right, #ddd 0%, #aaa 50%, #ddd 100%);
        animation: loading_animation 1s infinite linear;
    }

    .info-text {
        width: calc(100% - 166px);
        margin-left: 16px;
        padding: 18px;
        border-radius: 8px;
        background: #082435;
    }

    .text-header {
        display: flex;
        align-items: center;
        font-size: 14px;
        font-weight: 800;
        color: #00ffc2;
    }

    .text-header img {
        width: 30px; height: 30px; margin-right: 12px;
    }

    .info-text p {
        margin-top: 9px;
        font-size: 12px;
        color: #4d87aa;
    }

    .text-green-gradient {
        background: linear-gradient(255deg, #00ffc2 0%, #00aa6d 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        font-weight: 800;
    }

    .deposit-rate, .deposit-address {
        width: 100%;
        margin-top: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .rate-title, .address-title {
        font-size: 14px;
        font-weight: 700;
        color: #57819b;
        margin-bottom: 10px;
    }

    .rate-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .content-element {
        width: 200px;
        height: 50px;
        position: relative;
        padding: 1px;
        z-index: 1;
    }

    .content-element::before {
        content: '';
        position: absolute; top: 0; left: 0; width: 100%; height: 100%;
        background: linear-gradient(180deg, #04131f 0%, #223a4e 100%);
        clip-path: polygon(9px 0, calc(100% - 9px) 0, 100% 25%, 100% 75%, calc(100% - 9px) 100%, 9px 100%, 0 75%, 0 25%);
        z-index: -1;
    }

    .element-content {
        width: 100%; height: 100%;
        background: #072435;
        clip-path: polygon(9px 0, calc(100% - 9px) 0, 100% 25%, 100% 75%, calc(100% - 9px) 100%, 9px 100%, 0 75%, 0 25%);
        display: flex; align-items: center; padding: 0 15px;
    }

    .element-content input {
        background: transparent; border: none; color: white; width: 100%; margin-left: 10px; font-weight: 700;
    }

    .button-generate {
        margin-top: 15px;
        width: 220px;
        height: 50px;
        cursor: pointer;
        background: transparent;
        border: none;
    }

    .button-inner {
        width: 100%; height: 100%;
        background: linear-gradient(255deg, #00ffc2 0%, #00aa6d 100%);
        display: flex; justify-content: center; align-items: center;
        color: white; font-weight: 800;
        clip-path: polygon(10px 0, calc(100% - 10px) 0, 100% 25%, 100% 75%, calc(100% - 10px) 100%, 10px 100%, 0 75%, 0 25%);
    }

    .address-input {
        width: 100%; height: 60px; background: #071F2e; border-radius: 8px; position: relative;
    }

    .input-content input {
        width: 100%; height: 60px; padding: 0 50px 0 20px; color: white; background: transparent; border: none; font-family: monospace;
    }

    .input-content button {
        position: absolute; right: 15px; top: 50%; transform: translateY(-50%); background: transparent; border: none; cursor: pointer;
    }

    .input-content button svg { fill: #57819b; }

    @keyframes loading_animation {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
    }

    @media only screen and (max-width: 500px) {
        .deposit-info { flex-direction: column; }
        .info-text { width: 100%; margin: 15px 0 0 0; }
    }
</style>